import React, { FC, useEffect, useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Box,
  List,
  Modal,
  Button,
  ListItem,
  Accordion,
  TextField,
  Typography,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material'
import { IHeatmapRequest } from '../../../../shared/types/api/heatmap.type'

interface IProps {
  isOpen: boolean
  onClose: () => void
  onUpdate: (updatedParams: IHeatmapRequest) => void
  initialValues: IHeatmapRequest
}

const HeatmapSettingsModal: FC<IProps> = ({
  isOpen,
  onClose,
  onUpdate,
  initialValues,
}) => {
  const [formData, setFormData] = useState<IHeatmapRequest>(initialValues)
  const [errorMessages, setErrorMessages] = useState<{ [key: string]: string }>(
    {},
  )

  const getFieldUnit = (key: string) => {
    switch (key) {
      case 'alt':
        return '(m)'
      case 'rad':
        return '(km)'
      case 'frq':
        return '(MHz)'
      default:
        return ''
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target
    const keys = name.split('.')

    setFormData((prev) => {
      const updatedFormData = { ...prev } as any
      let current = updatedFormData

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]]
      }

      current[keys[keys.length - 1]] = isNaN(+value) ? value : +value
      return updatedFormData
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onUpdate(formData)
    onClose()
  }

  useEffect(() => {
    setFormData(initialValues)
  }, [initialValues])

  useEffect(() => {
    const newErrors: { [key: string]: string } = {}

    const validateField = (
      field: string,
      condition: boolean,
      message: string,
    ) => {
      if (condition) newErrors[field] = message
    }

    validateField(
      'alt',
      formData.transmitter.alt > 1000,
      'Maximum altitude exceeded (1000m)',
    )

    validateField(
      'frq',
      formData.transmitter.frq < 30 || formData.transmitter.frq > 50000,
      'Frequency value is out of range (30MHz - 50GHz)',
    )

    validateField(
      'rad',
      formData.output.rad <= 0 || formData.output.rad > 1000,
      'Maximum radius value exceeded (1000km)',
    )

    setErrorMessages((prev) => {
      const hasChanges =
        Object.keys(newErrors).some((key) => newErrors[key] !== prev[key]) ||
        Object.keys(prev).some((key) => !newErrors[key])
      return hasChanges ? newErrors : prev
    })
  }, [formData])

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
      >
        <Typography variant="h6" component="h2" gutterBottom>
          Change Heatmap Parameters
        </Typography>

        <List sx={{ listStyleType: 'disc', ml: 1 }}>
          {Object.keys(errorMessages).map(
            (key) =>
              errorMessages[key] && (
                <ListItem
                  sx={{
                    color: 'red',
                    fontSize: 12,
                    display: 'list-item',
                    padding: 0,
                  }}
                  key={key}
                >
                  {errorMessages[key]}
                </ListItem>
              ),
          )}
        </List>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <TextField
            fullWidth
            label="Site"
            name="site"
            value={formData.site}
            onChange={handleChange}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Network"
            name="network"
            value={formData.network}
            onChange={handleChange}
            margin="normal"
          />

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Transmitter</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                label="Transmitter Latitude"
                name="transmitter.lat"
                value={formData.transmitter.lat}
                margin="normal"
                disabled
              />
              <TextField
                fullWidth
                label="Transmitter Longitude"
                name="transmitter.lon"
                value={formData.transmitter.lon}
                margin="normal"
                disabled
              />
              {Object.keys(formData.transmitter).map((key) => {
                if (key !== 'lat' && key !== 'lon') {
                  return (
                    <TextField
                      key={key}
                      fullWidth
                      label={`Transmitter ${key} ${getFieldUnit(key)}`}
                      name={`transmitter.${key}`}
                      value={(formData.transmitter as any)[key]}
                      onChange={handleChange}
                      margin="normal"
                    />
                  )
                }
                return null
              })}
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Receiver</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {Object.keys(formData.receiver).map((key) => (
                <TextField
                  key={key}
                  fullWidth
                  label={`Receiver ${key}`}
                  name={`receiver.${key}`}
                  value={(formData.receiver as any)[key]}
                  onChange={handleChange}
                  margin="normal"
                />
              ))}
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Antenna</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {Object.keys(formData.antenna).map((key) => (
                <TextField
                  key={key}
                  fullWidth
                  label={`Antenna ${key}`}
                  name={`antenna.${key}`}
                  value={(formData.antenna as any)[key]}
                  onChange={handleChange}
                  margin="normal"
                />
              ))}
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Output</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {Object.keys(formData.output).map((key) => (
                <TextField
                  key={key}
                  fullWidth
                  label={`Output ${key} ${getFieldUnit(key)}`}
                  name={`output.${key}`}
                  value={(formData.output as any)[key]}
                  onChange={handleChange}
                  margin="normal"
                />
              ))}
            </AccordionDetails>
          </Accordion>
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!!Object.values(errorMessages).length}
          sx={{ mt: 2 }}
        >
          Save
        </Button>
      </Box>
    </Modal>
  )
}

export default HeatmapSettingsModal
